import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { BRAND_COLOR, Empty, fmtDate } from "./EnquiriesPanel";

const BRANDS = [
  { id: "malansh", label: "Malansh Solutions" },
  { id: "artwork360", label: "Artwork 360" },
  { id: "medlyn360", label: "Medlyn 360" },
];
const EMPTY = { title: "", brand: "malansh", employment_type: "Full-time", location: "Mumbai, India · Hybrid", summary: "", description: "", requirements: "", status: "open" };

const toForm = (j) => ({ ...j, requirements: (j.requirements || []).join("\n") });
const toPayload = (f) => ({ ...f, requirements: f.requirements.split("\n").map((s) => s.trim()).filter(Boolean) });

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-indigo/60">{label}</span>
    {children}
  </label>
);
const cls = "w-full rounded-xl border border-brand-indigo/15 bg-brand-ice px-4 py-3 text-sm text-brand-ink outline-none focus:border-brand-cyan";

const JobForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(f);
      const { data } = f.id ? await api.put(`/jobs/${f.id}`, payload) : await api.post("/jobs", payload);
      toast.success(f.id ? "Job updated" : "Job posted");
      onSaved(data, !!f.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="job-form" onSubmit={submit} className="rounded-3xl border border-brand-indigo/15 bg-white p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold text-brand-ink">{f.id ? "Edit job" : "New job posting"}</h3>
        <button type="button" data-testid="job-form-cancel" onClick={onCancel} className="grid h-9 w-9 place-items-center rounded-full border border-brand-indigo/15 text-brand-indigo hover:bg-brand-ice"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field label="Job title *"><input data-testid="job-form-title" required minLength={3} value={f.title} onChange={set("title")} className={cls} placeholder="Virtual Medical Scribe" /></Field>
        <Field label="Brand *">
          <select data-testid="job-form-brand" value={f.brand} onChange={set("brand")} className={cls}>{BRANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}</select>
        </Field>
        <Field label="Employment type"><input data-testid="job-form-type" value={f.employment_type} onChange={set("employment_type")} className={cls} placeholder="Full-time" /></Field>
        <Field label="Location"><input data-testid="job-form-location" value={f.location} onChange={set("location")} className={cls} placeholder="Mumbai, India · Hybrid" /></Field>
        <div className="sm:col-span-2"><Field label="One-line summary * (shown on the careers list)"><input data-testid="job-form-summary" required minLength={10} value={f.summary} onChange={set("summary")} className={cls} /></Field></div>
        <div className="sm:col-span-2"><Field label="Full description *"><textarea data-testid="job-form-description" required minLength={10} rows={6} value={f.description} onChange={set("description")} className={cls} /></Field></div>
        <div className="sm:col-span-2"><Field label="Requirements (one per line)"><textarea data-testid="job-form-requirements" rows={5} value={f.requirements} onChange={set("requirements")} className={cls} placeholder={"3+ years experience\nExcellent English"} /></Field></div>
        <Field label="Status">
          <select data-testid="job-form-status" value={f.status} onChange={set("status")} className={cls}><option value="open">Open — visible on careers page</option><option value="closed">Closed — hidden</option></select>
        </Field>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button type="submit" data-testid="job-form-submit" disabled={busy} className="rounded-full bg-brand-indigo px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-corn disabled:opacity-60">{busy ? "Saving…" : f.id ? "Save changes" : "Publish job"}</button>
      </div>
    </form>
  );
};

export const JobsPanel = () => {
  const [jobs, setJobs] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/jobs", { params: { all: true } }).then((r) => setJobs(r.data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const onSaved = (job, isEdit) => {
    setJobs((list) => (isEdit ? list.map((j) => (j.id === job.id ? job : j)) : [job, ...list]));
    setEditing(null);
  };

  const toggle = async (j) => {
    try {
      const { data } = await api.put(`/jobs/${j.id}`, { ...j, status: j.status === "open" ? "closed" : "open" });
      setJobs((list) => list.map((x) => (x.id === j.id ? data : x)));
      toast.success(data.status === "open" ? "Job reopened" : "Job closed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete "${j.title}"? Applications already received are kept.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      setJobs((list) => list.filter((x) => x.id !== j.id));
      toast.success("Job deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-jobs-panel">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">Job postings</h2>
        {!editing && (
          <button data-testid="job-new-button" onClick={() => setEditing(EMPTY)} className="inline-flex items-center gap-2 rounded-full bg-brand-indigo px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-corn"><Plus className="h-4 w-4" />New job</button>
        )}
      </div>

      {editing && <div className="mt-8"><JobForm initial={editing} onSaved={onSaved} onCancel={() => setEditing(null)} /></div>}

      <div className="mt-8 space-y-4">
        {jobs === null && <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-indigo/60">Loading jobs…</div>}
        {jobs && jobs.length === 0 && <Empty testId="jobs-empty" text="No job postings yet. Click “New job” to add one." />}
        {jobs?.map((j) => (
          <article key={j.id} data-testid={`job-row-${j.id}`} className={`rounded-2xl border p-5 sm:p-6 ${j.status === "open" ? "border-brand-indigo/15 bg-white" : "border-brand-indigo/10 bg-white/60"}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-lg font-semibold text-brand-ink">{j.title}</h3>
                  <span className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/70"><span className="h-2 w-2 rounded-full" style={{ background: BRAND_COLOR[j.brand] }} />{BRANDS.find((b) => b.id === j.brand)?.label}</span>
                  <span data-testid={`job-status-${j.id}`} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${j.status === "open" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{j.status}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500"><span>{j.employment_type}</span><span>{j.location}</span><span>Posted {fmtDate(j.created_at)}</span></div>
                <p className="mt-3 text-sm text-slate-700">{j.summary}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`job-edit-${j.id}`} onClick={() => setEditing(toForm(j))} className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo hover:bg-brand-ice"><Pencil className="h-3.5 w-3.5" />Edit</button>
                <button data-testid={`job-toggle-${j.id}`} onClick={() => toggle(j)} className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo hover:bg-brand-ice">{j.status === "open" ? <><EyeOff className="h-3.5 w-3.5" />Close</> : <><Eye className="h-3.5 w-3.5" />Reopen</>}</button>
                <button data-testid={`job-delete-${j.id}`} onClick={() => remove(j)} className="grid h-9 w-9 place-items-center rounded-full border border-red-200 text-red-500 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
